import React from 'react';
import { Popper } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactCountryFlag from 'react-country-flag';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { IMenuItem } from 'types/menu';
import { IAppState } from 'store/reducers/rootReducer';
import { addFavorite, removeFavorite } from 'store/actions/favorites';
import { getModuleIcon, generateMenuItemId } from './utils';
import { LANGUAGE_FLAGS, faStar, faStarOutline } from './constants';
import {
  PopperContent,
  DropdownHeader,
  DropdownTopRow,
  DropdownFlag,
  DropdownLanguage,
  DropdownCourseName,
  ModulesGrid,
  ModuleItem as BaseModuleItem,
  ModuleIconWrapper,
  ModuleName,
} from './styles';

interface Props {
  anchorEl: HTMLElement | null;
  modules: IMenuItem[];
  courseInfo: { language: string; courseName: string } | null;
  onModuleClick: (module: IMenuItem) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export const ModuleDropdown: React.FC<Props> = ({
  anchorEl,
  modules,
  courseInfo,
  onModuleClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const dispatch = useDispatch();
  const favouritesTabs = useSelector((state: IAppState) => state.favouritesTabs);

  const handleToggleFavorite = (e: React.MouseEvent, module: IMenuItem) => {
    e.stopPropagation();

    if (!courseInfo) return;

    const moduleId = generateMenuItemId(module);
    const isFavorite = !!favouritesTabs.items[moduleId];

    if (isFavorite) {
      dispatch(removeFavorite(moduleId));
    } else {
      dispatch(
        addFavorite({
          moduleId,
          module,
          language: courseInfo.language,
          courseName: courseInfo.courseName,
          addedAt: Date.now(),
        }),
      );
    }
  };

  // Sort modules alphabetically by title
  const sortedModules = [...modules].sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="right-start"
      modifiers={{
        offset: {
          enabled: true,
          offset: '8, 0',
        },
        preventOverflow: {
          enabled: true,
          boundariesElement: 'viewport',
        },
        flip: {
          enabled: true,
        },
      }}
    >
      <PopperContent moduleCount={modules.length} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {courseInfo && (
          <DropdownHeader>
            <DropdownTopRow>
              <DropdownFlag>
                <ReactCountryFlag
                  countryCode={LANGUAGE_FLAGS[courseInfo.language] || 'XX'}
                  svg
                  style={{
                    width: '16px',
                    height: '11px',
                  }}
                />
              </DropdownFlag>
              <DropdownLanguage>{courseInfo.language}</DropdownLanguage>
            </DropdownTopRow>
            <DropdownCourseName>{courseInfo.courseName}</DropdownCourseName>
          </DropdownHeader>
        )}
        <ModulesGrid moduleCount={modules.length}>
          {sortedModules.map((module) => {
            const moduleId = generateMenuItemId(module);
            const isFavorite = !!favouritesTabs.items[moduleId];

            return (
              <ModuleItem key={moduleId} onClick={() => onModuleClick(module)}>
                <ModuleIconWrapper>
                  <FontAwesomeIcon icon={getModuleIcon(module.title)} />
                </ModuleIconWrapper>
                <ModuleName>{module.title}</ModuleName>
                <StarButton
                  onClick={(e) => handleToggleFavorite(e, module)}
                  isFavorite={isFavorite}
                  title={isFavorite ? 'Remove from favourites' : 'Add to favourites'}
                >
                  <FontAwesomeIcon icon={isFavorite ? faStar : faStarOutline} />
                </StarButton>
              </ModuleItem>
            );
          })}
        </ModulesGrid>
      </PopperContent>
    </Popper>
  );
};

const ModuleItem = styled(BaseModuleItem)`
  position: relative;
  padding-right: 40px;
`;

const StarButton = styled.button<{ isFavorite: boolean }>`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${(props) => (props.isFavorite ? '#ffb700' : '#6b7280')};
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover {
    color: ${(props) => (props.isFavorite ? '#ffd700' : '#ffb700')};
    background: rgba(255, 183, 0, 0.1);
  }

  svg {
    font-size: 14px;
  }
`;
